import i18next from 'i18next';
import { MemberPreference } from '../profile/model/MemberPreference';
import { NotificationSettings } from '../profile/model/NotificationSettings';
import { NotificationsType } from '../profile/model/NotificationsType';
import { PreferenceType } from '../profile/model/PreferenceType';

const translate = (translateKey: string) => i18next.t(translateKey);

export const getCategoryTranslation = (category: string): string => {
  switch (category) {
    case 'Community feed':
      return translate('notifications_settings_category_community_feed');
    case 'Companion requests':
      return translate('notifications_settings_category_companion_requests');
    case 'Stories':
      return translate('notifications_settings_category_stories');
    case 'carepoints':
      return translate('notifications_settings_category_carepoints');
    case 'Polls':
      return translate('notifications_settings_category_polls');
    case 'Conversations':
      return translate('notifications_settings_category_conversations');
    default:
      return category;
  }
};

export const convertPreferencesToNotificationsSettings = (
  memberPreferences: MemberPreference[]
) => {
  const defaultNotificationsSettings: NotificationSettings[] = [
    {
      category: 'Community feed',
      notifications: [
        {
          type: PreferenceType.comms_ReplyOnMyPost,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_ReactionOnMyPost,
          values: [{ value: 'false' }],
        },
      ],
    },
    {
      category: 'Companion requests',
      notifications: [
        {
          type: PreferenceType.comms_SomeoneAcceptsMyRequest,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_SomeoneRejectsMyRequest,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_IReceiveACompanionRequest,
          values: [{ value: 'false' }],
        },
      ],
    },
    {
      category: 'Stories',
      notifications: [
        {
          type: PreferenceType.comms_SomeoneCommentsOnMyStory,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_SomeoneReactsOnMyStory,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_SomeonePublishesAStory,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_NewStoriesFromTheAuthorsIFollow,
          values: [{ value: 'false' }],
        },
      ],
    },
    {
      category: 'carepoints',
      notifications: [
        {
          type: PreferenceType.comms_IReceivecarepointsReward,
          values: [{ value: 'false' }],
        },
      ],
    },
    {
      category: 'Polls',
      notifications: [
        {
          type: PreferenceType.comms_MyPollHasNewResponses,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_MyPollHasBeenClosed,
          values: [{ value: 'false' }],
        },
        {
          type: PreferenceType.comms_NewPollInRoomsIFollow,
          values: [{ value: 'false' }],
        },
      ],
    },
    {
      category: 'Conversations',
      notifications: [
        {
          type: PreferenceType.comms_SomeoneStartsAConversation,
          values: [{ value: 'false' }],
        },
      ],
    },
  ];

  const convertedNotificationsSettings: NotificationSettings[] =
    defaultNotificationsSettings.map((notificationSettings) => {
      return {
        category: notificationSettings.category,
        notifications: notificationSettings.notifications.map(
          (notification) => {
            return {
              type: notification.type,
              values: memberPreferences.find(
                (memberPreference) =>
                  memberPreference.type === notification.type
              )?.values || [{ value: 'false' }],
            };
          }
        ),
      };
    });
  return convertedNotificationsSettings;
};

export const getNotificationDescription = (type: PreferenceType) => {
  switch (type) {
    case PreferenceType.comms_ReplyOnMyPost:
      return translate('notifications_settings_reply_on_my_post_label');
    case PreferenceType.comms_ReactionOnMyPost:
      return translate('notifications_settings_reaction_on_my_post_label');
    case PreferenceType.comms_SomeoneAcceptsMyRequest:
      return translate(
        'notifications_settings_someone_accepts_my_request_label'
      );
    case PreferenceType.comms_SomeoneRejectsMyRequest:
      return translate(
        'notifications_settings_someone_rejects_my_request_label'
      );
    case PreferenceType.comms_IReceiveACompanionRequest:
      return translate(
        'notifications_settings_i_receive_a_companion_request_label'
      );
    case PreferenceType.comms_SomeoneCommentsOnMyStory:
      return translate(
        'notifications_settings_someone_comments_on_my_story_label'
      );
    case PreferenceType.comms_SomeoneReactsOnMyStory:
      return translate(
        'notifications_settings_someone_reacts_on_my_story_label'
      );
    case PreferenceType.comms_SomeonePublishesAStory:
      return translate(
        'notifications_settings_someone_publishes_a_story_label'
      );
    case PreferenceType.comms_NewStoriesFromTheAuthorsIFollow:
      return translate(
        'notifications_settings_new_stories_from_the_author_i_follow_label'
      );
    case PreferenceType.comms_IReceivecarepointsReward:
      return translate(
        'notifications_settings_i_receive_carepoints_reward_label'
      );

    case PreferenceType.comms_MyPollHasNewResponses:
      return translate(
        'notifications_settings_my_poll_has_new_responses_label'
      );
    case PreferenceType.comms_MyPollHasBeenClosed:
      return translate('notifications_settings_my_poll_has_been_closed_label');
    case PreferenceType.comms_NewPollInRoomsIFollow:
      return translate(
        'notifications_settings_new_poll_in_rooms_i_follow_label'
      );
    case PreferenceType.comms_SomeoneStartsAConversation:
      return translate(
        'notifications_settings_someone_starts_a_conversation_label'
      );
    default:
      return;
  }
};

export const convertNotificationsSettingsToPreferences = (
  notificationsSettings: NotificationSettings[]
): MemberPreference[] => {
  const preferences = notificationsSettings.map(
    (notificationSettings) => notificationSettings.notifications
  );

  return preferences.reduce((a, b) => {
    return a.concat(b);
  }, []);
};

export const areAllNotificationsSettingsAllowed = (
  notificationsSettings: NotificationSettings[],
  notificationType: NotificationsType
) => {
  return !notificationsSettings.some((notificationsSetting) =>
    notificationsSetting.notifications.some((notification) =>
      notificationType === NotificationsType.carecircle
        ? notification.values[1].value === 'false'
        : notification.values[0].value === 'false'
    )
  );
};

export const setAllNotifications = (
  notificationsSettings: NotificationSettings[],
  notificationType: NotificationsType,
  areAllNotificationsAllowed: boolean
): MemberPreference[] => {
  const memberPreferences = notificationsSettings.map((notificationsSetting) =>
    notificationsSetting.notifications.map((notification) => {
      return {
        type: notification.type,
        values: [
          {
            value:
              notificationType === NotificationsType.carecircle
                ? notification.values[0].value
                : areAllNotificationsAllowed
                ? 'false'
                : 'true',
          },
          {
            value:
              notificationType === NotificationsType.carecircle
                ? areAllNotificationsAllowed
                  ? 'false'
                  : 'true'
                : notification.values[0].value,
          },
        ],
      };
    })
  );

  return memberPreferences.reduce((a, b) => {
    return a.concat(b);
  }, []);
};
